import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit, Trash2, Video, FileText, HelpCircle, Upload, Save, X, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
// import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

const MATERIAL_TYPES = [
  { type: 'video', label: 'Video', icon: Video, color: 'text-blue-600 bg-blue-50' },
  { type: 'document', label: 'Document', icon: FileText, color: 'text-green-600 bg-green-50' },
  { type: 'quiz', label: 'Quiz', icon: HelpCircle, color: 'text-purple-600 bg-purple-50' },
];

function MaterialRow({ material, onDelete }) {
  const mt = MATERIAL_TYPES.find(t => t.type === material.type) || MATERIAL_TYPES[0];
  const Icon = mt.icon;
  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-lg bg-background border text-sm">
      <div className={`w-7 h-7 rounded-md flex items-center justify-center ${mt.color}`}><Icon className="w-3.5 h-3.5" /></div>
      <span className="flex-1 font-medium truncate">{material.title}</span>
      <Badge variant="secondary" className="text-xs capitalize">{material.type}</Badge>
      <button onClick={onDelete} className="text-muted-foreground hover:text-destructive transition-colors"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function ModuleRow({ module, index, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(module.module);
  const [lessons, setLessons] = useState(String(module.lessons));
  const [duration, setDuration] = useState(module.duration);
  const [materials, setMaterials] = useState(module.materials || []);
  const [addingMaterial, setAddingMaterial] = useState(false);
  const [matTitle, setMatTitle] = useState('');
  const [matType, setMatType] = useState('video');

  const save = () => {
    onUpdate(index, { ...module, module: title, lessons: Number(lessons), duration, materials });
    setEditing(false);
  };

  const addMaterial = () => {
    if (!matTitle.trim()) return;
    const updated = [...materials, { title: matTitle, type: matType }];
    setMaterials(updated);
    onUpdate(index, { ...module, materials: updated });
    setMatTitle(''); setAddingMaterial(false);
  };

  const deleteMaterial = (mi) => {
    const updated = materials.filter((_, i) => i !== mi);
    setMaterials(updated);
    onUpdate(index, { ...module, materials: updated });
  };

  return (
    <div className="bg-muted/30 rounded-xl border overflow-hidden">
      <div className="flex items-center gap-3 p-3">
        <GripVertical className="w-4 h-4 text-muted-foreground/40 cursor-grab" />
        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">{index + 1}</div>
        {editing ? (
          <div className="flex-1 flex items-center gap-2">
            <Input value={title} onChange={e => setTitle(e.target.value)} className="h-7 text-sm" />
            <Input value={lessons} onChange={e => setLessons(e.target.value)} placeholder="Lessons" className="h-7 text-sm w-20" type="number" />
            <Input value={duration} onChange={e => setDuration(e.target.value)} placeholder="Duration" className="h-7 text-sm w-24" />
          </div>
        ) : (
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{module.module}</p>
            <p className="text-xs text-muted-foreground">{module.lessons} lessons · {module.duration}</p>
          </div>
        )}
        <div className="flex items-center gap-1 flex-shrink-0">
          {editing ? (
            <>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={save}><Save className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(false)}><X className="w-3.5 h-3.5" /></Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(true)}><Edit className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete(index)}><Trash2 className="w-3.5 h-3.5" /></Button>
              <button onClick={() => setExpanded(!expanded)} className="text-muted-foreground p-1">
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-4 pb-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Learning Materials</p>
              {materials.map((m, mi) => (
                <MaterialRow key={mi} material={m} onDelete={() => deleteMaterial(mi)} />
              ))}
              {addingMaterial ? (
                <div className="flex gap-2 items-center">
                  <Input value={matTitle} onChange={e => setMatTitle(e.target.value)} placeholder="Material title" className="h-8 text-sm flex-1" />
                  <select value={matType} onChange={e => setMatType(e.target.value)} className="h-8 text-sm rounded-md border bg-background px-2">
                    {MATERIAL_TYPES.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
                  </select>
                  <Button size="sm" className="h-8 gap-1" onClick={addMaterial}><Save className="w-3 h-3" /> Add</Button>
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => setAddingMaterial(false)}><X className="w-3 h-3" /></Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" className="gap-1.5 w-full" onClick={() => setAddingMaterial(true)}>
                  <Upload className="w-3 h-3" /> Add Material (Video / Doc / Quiz)
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ModuleManager({ course, onBack }) {
  const [modules, setModules] = useState(
    (course.curriculum || []).map(m => ({ ...m, materials: m.materials || [] }))
  );
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [saved, setSaved] = useState(false);

  const updateModule = (index, updated) => {
    setModules(prev => prev.map((m, i) => i === index ? updated : m));
  };

  const deleteModule = (index) => {
    setModules(prev => prev.filter((_, i) => i !== index));
  };

  const addModule = () => {
    if (!newTitle.trim()) return;
    setModules(prev => [...prev, { module: newTitle, lessons: 0, duration: '0h', materials: [] }]);
    setNewTitle(''); setAdding(false);
  };

  const saveAll = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1">
            ← Back to course
          </button>
          <h2 className="font-heading text-lg font-bold">Module Manager — {course.title}</h2>
          <p className="text-xs text-muted-foreground">{modules.length} modules · Edit, reorder, add materials</p>
        </div>
        <Button onClick={saveAll} className={`gap-2 ${saved ? 'bg-green-600 hover:bg-green-700' : ''}`}>
          <Save className="w-4 h-4" /> {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      <div className="space-y-3">
        {modules.map((mod, i) => (
          <ModuleRow key={i} module={mod} index={i} onUpdate={updateModule} onDelete={deleteModule} />
        ))}
      </div>

      {adding ? (
        <div className="flex gap-2">
          <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="New module title..." className="flex-1" />
          <Button onClick={addModule} className="gap-1"><Plus className="w-4 h-4" /> Add</Button>
          <Button variant="outline" onClick={() => setAdding(false)}><X className="w-4 h-4" /></Button>
        </div>
      ) : (
        <Button variant="outline" className="w-full gap-2 border-dashed" onClick={() => setAdding(true)}>
          <Plus className="w-4 h-4" /> Add New Module
        </Button>
      )}
    </div>
  );
}